import React, { useEffect, useState } from 'react';
import DataTable from '../../components/common/DataTable/DataTable';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input'; // Import Input
import { toast, ToastContainer } from 'react-toastify';
import { educationCategoryService } from '../../services/educationCategoryService';
import EducationCategoryForm from './EducationCategoryForm';
import styles from './EducationCategory.module.css';
import { usePermission } from '../../hooks/usePermissions';
import { formatLocalDateTime } from '../../utils/datetime';

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);

const EducationCategoryList = () => {
    const [categories, setCategories] = useState([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState(null);
    const { hasPermission } = usePermission()

    // Filters and Pagination State
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [limit] = useState(10);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('DESC');

    const fetchCategories = async () => {
        try {
            const params = {
                page,
                limit,
                search: search || undefined,
                sortBy,
                sortOrder
            };
            const response = await educationCategoryService.getAll(params);

            // Check if response has pagination data (new structure)
            if (response && response.status && response.data) {
                if (response.pagination) {
                    setCategories(response.data);
                    setTotalPages(response.pagination.totalPages);
                } else {
                    setCategories(Array.isArray(response.data) ? response.data : []);
                }
            } else {
                console.error("Unexpected API response structure:", response);
                setCategories([]);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            // toast.error("Failed to fetch categories");
        }
    };

    useEffect(() => {
        fetchCategories();
    }, [page, sortBy, sortOrder]); // Re-fetch when page or sort changes

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCategories();
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const handleAdd = () => {
        setCategoryToEdit(null);
        setIsFormOpen(true);
    };

    const handleEdit = (category) => {
        setCategoryToEdit(category);
        setIsFormOpen(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this category?")) return;
        try {
            await educationCategoryService.delete(id);
            toast.success("Category deleted successfully");
            fetchCategories();
        } catch (error) {
            console.error("Error deleting category:", error);
            toast.error("Failed to delete category");
        }
    };

    const handleSort = (key) => {
        if (sortBy === key) {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortBy(key);
            setSortOrder('ASC');
        }
    };

    const columns = [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'topic', label: 'Topic', sortable: true },
        { 
            key: 'display', 
            label: 'Display', 
            sortable: true,
            render: (val) => val ? 'True' : 'False'
        },
        { key: 'sort_order', label: 'Sort Order', sortable: true },
        {
            key: 'created_at',
            label: 'Created At',
            sortable: true,
            render: (val) => formatLocalDateTime(val, '-')
        },
        {
            key: 'updated_at',
            label: 'Updated At',
            sortable: true,
            render: (val) => formatLocalDateTime(val, '-')
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className={styles.flexGap8}>
                    {hasPermission("MODIFY_EDUCATIONCATEGORY") && (
                        <button className={styles.iconBtn} onClick={() => handleEdit(row)} title="Edit Category">
                            <EditIcon />
                        </button>
                    )}
                    {hasPermission("DELETE_EDUCATIONCATEGORY") && (
                        <button className={`${styles.iconBtn} ${styles.deleteBtn}`} onClick={() => handleDelete(row.id)} title="Delete Category">
                            <TrashIcon />
                        </button>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className={styles.container}>
            <ToastContainer position="top-center" autoClose={2000} />

            <div className={styles.header}>
                <h2>Education Categories</h2>
                <div className={styles.flexCenterGap10}>
                    <Input
                        placeholder="Search categories..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(1); // Reset to page 1 on search
                        }}
                    />
                    {hasPermission("CREATE_EDUCATIONCATEGORY") && <Button onClick={handleAdd} >+ Add Category</Button>}
                </div>
            </div>

            <DataTable
                columns={columns}
                data={categories}
                onSort={handleSort}
                sortBy={sortBy}
                sortOrder={sortOrder}
            />

            {/* Pagination Controls */}
            <div className={styles.imTableFooter}>
                <span>Showing {categories.length} records</span>

                {totalPages > 1 && (
                    <div className={styles.imPagination}>
                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {"<"}
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                            if (
                                p === 1 ||
                                p === totalPages ||
                                (p >= page - 1 && p <= page + 1)
                            ) {
                                return (
                                    <button
                                        type="button"
                                        key={p}
                                        className={`${styles.imPageBtn} ${page === p ? styles.active : ""}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            }

                            if (p === page - 2 || p === page + 2) {
                                return (
                                    <span key={p} className={styles.paginationEllipsis}>
                                        ...
                                    </span>
                                );
                            }

                            return null;
                        })}

                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page >= totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            {">"}
                        </button>
                    </div>
                )}

                <span className={`${styles.textMuted} ${styles.textSmall}`}>
                    Page {page} of {totalPages || 1}
                </span>
            </div>

            <EducationCategoryForm
                isOpen={isFormOpen}
                onClose={() => setIsFormOpen(false)}
                categoryToEdit={categoryToEdit}
                refreshData={fetchCategories}
            />
        </div>
    );
};

export default EducationCategoryList;
